import {
  HAND_CARD_SCALE,
  HAND_FAN_BOX_H,
  handCardPlacement,
  handFanBudget,
  handFanLayout,
} from "./hand-fan";

/*
 * YOUR HAND AT THE هفت خبیث TABLE, WHICH CAN GROW PAST TWENTY CARDS: a 7 chain, a catch and a blind take all
 * add to it, and the shared fan (`hand-fan.ts`, untouched) gives every card less room as it grows: at
 * 25 cards a step of about 12 px. So:
 *   below {@link TWO_ROWS_AT} the shared fan, as every card table draws it, with شلم's cap on its arc;
 *   from it two flat, overlapping rows in the fan's OWN box — the second half of the sorted hand in
 *   front, the first half behind it, its top band (index and all) showing — so every card keeps a
 *   usable tap target on a 320 px screen, 30 cards included (`haftkhabis-hand.test.ts`, and the layout
 *   probe's `elementFromPoint` at every card).
 * Pure: the numbers are the test's and the browser's alike.
 */

/** At this many cards the hand takes two rows (the operator's brief: «at 15+ cards»). */
export const TWO_ROWS_AT = 15;
/** …and earlier on a narrow table: a fan whose step falls under this is no tap target (a 12-card fan at 240 px). */
export const MIN_FAN_STEP = 14;
/** A fan whose edge cards sit further out is arced as one whose edges sit here (شلم's `FAN_OFF_CAP`). */
export const HAND_OFF_CAP = 5;
/** The card a row is drawn with: the `large` 50×70 box at the fan's scale. */
const CARD_W = 50;
const CARD_H = 70;
const SCALED_W = CARD_W * HAND_CARD_SCALE;
const SCALED_H = CARD_H * HAND_CARD_SCALE;
/** Two rows take the panel's width less its padding (`px-3`, the fan's own `px-2` dropped) and 2 px of air. */
const ROW_INSET = 2 * (12 + 2);
/** A row's neighbours are at most this far apart: the fan's 28 px. */
const MAX_STEP = 28;
/** The back row's foot over the box's bottom: its top band shows above the front row. */
export const BACK_ROW_LIFT = HAND_FAN_BOX_H - SCALED_H;
/** How far a legal card in the FRONT row may rise: it must not take the band of the card behind it. */
export const FRONT_ROW_RISE = 6;

export interface HandPlace {
  readonly left: number;
  readonly transformOrigin: string;
  readonly transform: string;
  /** Absent in the fan: it sits on the box's bottom. */
  readonly bottom?: number;
}

export interface HandLayout {
  readonly rows: 1 | 2;
  /** The box's width (px). */
  readonly width: number;
  /** Card `i` of the sorted hand, `rise` px over its place (a two-row hand caps it). */
  readonly place: (i: number, rise: number) => HandPlace;
  /** Which row card `i` is in: 0 the back, 1 the front. A fan is all front. */
  readonly rowOf: (i: number) => 0 | 1;
  /** Px of card `i` that show across, whatever covers the rest: the tap target's width. */
  readonly exposedW: (i: number) => number;
  /** …and down. */
  readonly exposedH: (i: number) => number;
}

/** How many cards of a two-row hand ride in the back row: the front holds the odd one. */
export const backRowCount = (n: number): number => Math.floor(n / 2);

/**
 * The hand for `n` cards on a table `tableW` px wide (null before it is measured: the fan's own 340
 * budget, and a two-row hand as wide as a 320 px screen leaves it).
 */
export function handLayout(n: number, tableW: number | null): HandLayout {
  const budget = tableW === null ? undefined : handFanBudget(tableW, HAND_CARD_SCALE, HAND_OFF_CAP);
  const fan = handFanLayout(n, HAND_CARD_SCALE, budget);
  const step = fan.step;
  if (n < TWO_ROWS_AT && (n < 2 || step >= MIN_FAN_STEP)) {
    return {
      rows: 1,
      width: fan.width,
      place: (i, rise) => handCardPlacement(i, n, step, rise, HAND_CARD_SCALE, HAND_OFF_CAP),
      rowOf: () => 1,
      exposedW: (i) => (i === n - 1 ? SCALED_W : step),
      exposedH: () => SCALED_H,
    };
  }
  const roomW = (tableW ?? 320) - ROW_INSET;
  const back = backRowCount(n);
  const front = n - back;
  const stepOf = (count: number): number =>
    count > 1 ? Math.min(MAX_STEP, (roomW - SCALED_W) / (count - 1)) : 0;
  const widthOf = (count: number): number => (count - 1) * stepOf(count) + SCALED_W;
  const width = Math.max(widthOf(back), widthOf(front));
  const rowOf = (i: number): 0 | 1 => (i < back ? 0 : 1);
  const slotOf = (i: number): { index: number; count: number } =>
    i < back ? { index: i, count: back } : { index: i - back, count: front };
  return {
    rows: 2,
    width,
    rowOf,
    place: (i, rise) => {
      const { index, count } = slotOf(i);
      // the row centred in the box; the card's scale grows about its foot's centre, so its box starts
      // (SCALED_W − CARD_W) / 2 in from where its footprint does
      const left = (width - widthOf(count)) / 2 + index * stepOf(count) + (SCALED_W - CARD_W) / 2;
      const up = rowOf(i) === 1 ? Math.min(rise, FRONT_ROW_RISE) : 0;
      return {
        left,
        bottom: rowOf(i) === 0 ? BACK_ROW_LIFT : 0,
        transformOrigin: "bottom center",
        transform: `translateY(${-up}px) scale(${HAND_CARD_SCALE})`,
      };
    },
    exposedW: (i) => {
      const { index, count } = slotOf(i);
      return index === count - 1 ? SCALED_W : stepOf(count);
    },
    exposedH: (i) => (rowOf(i) === 0 ? BACK_ROW_LIFT : SCALED_H),
  };
}

/*
 * THE BLIND TAKE'S CARDS (the operator: «far too small: make them big and easy to tap»): your own hand's large card, in
 * as many rows as that takes, so a finger always has more than 44 px across and 60 px down of every card. A row is only
 * given up for a smaller card where the room has no height for one more row; never under 44 px.
 */
/** The tap target, as measured on the page: every card shows at least this much. */
export const TAKE_TAP_W = 44;
export const TAKE_TAP_H = 60;
/** The step that leaves that, whatever fraction of a px a row's centring starts on. */
const TAKE_STEP_MIN = TAKE_TAP_W + 2;
const TAKE_BAND_MIN = TAKE_TAP_H + 2;
/** A card this wide is still «about your hand's»: a row keeps it. */
const TAKE_GOOD_W = 54;
const TAKE_ASPECT = CARD_H / CARD_W;
/** Neighbours that do not overlap stand this far apart. */
const TAKE_AIR = 6;

export interface TakeLayout {
  readonly rows: number;
  readonly cardW: number;
  readonly cardH: number;
  readonly width: number;
  readonly height: number;
  /** All of it inside the room it was given (else: the closest there is, its targets still whole). */
  readonly fits: boolean;
  /** Card `i`'s place. A later card lies over an earlier one, a lower row over the row above. */
  readonly place: (i: number) => { readonly left: number; readonly top: number };
  /** Px of card `i` no other card covers: its tap target. */
  readonly tapW: (i: number) => number;
  readonly tapH: (i: number) => number;
}

/** `n` cards in `roomW` × `roomH` px. */
export function takeLayout(n: number, roomW: number, roomH: number): TakeLayout {
  const count = Math.max(0, n);
  const plan = (rows: number): TakeLayout => {
    const perRow = Math.max(1, Math.ceil(count / rows));
    // the row keeps its step: the card takes what that leaves, up to the hand's own
    const left = perRow > 1 ? roomW - (perRow - 1) * TAKE_STEP_MIN : roomW;
    const cardW = Math.max(TAKE_TAP_W, Math.min(SCALED_W, left));
    const cardH = cardW * TAKE_ASPECT;
    const stepX =
      perRow > 1
        ? Math.max(TAKE_STEP_MIN, Math.min(cardW + TAKE_AIR, (roomW - cardW) / (perRow - 1)))
        : 0;
    const stepY =
      rows > 1
        ? Math.max(TAKE_BAND_MIN, Math.min(cardH + TAKE_AIR, (roomH - cardH) / (rows - 1)))
        : 0;
    const rowOf = (i: number): number => Math.floor(i / perRow);
    const inRow = (row: number): number => Math.min(perRow, Math.max(0, count - row * perRow));
    const width = (perRow - 1) * stepX + cardW;
    const height = (rows - 1) * stepY + cardH;
    return {
      rows,
      cardW,
      cardH,
      width,
      height,
      fits: width <= roomW + 0.01 && height <= roomH + 0.01,
      place: (i) => {
        const row = rowOf(i);
        const rowW = (inRow(row) - 1) * stepX + cardW;
        return { left: (width - rowW) / 2 + (i - row * perRow) * stepX, top: row * stepY };
      },
      tapW: (i) => {
        const row = rowOf(i);
        return i - row * perRow === inRow(row) - 1 ? cardW : Math.min(cardW, stepX);
      },
      tapH: (i) => (rowOf(i) === rows - 1 ? cardH : Math.min(cardH, stepY)),
    };
  };
  const most = Math.max(1, count);
  const candidates = Array.from({ length: most }, (_u, i) => plan(i + 1));
  return (
    candidates.find((c) => c.fits && c.cardW >= TAKE_GOOD_W) ??
    candidates.find((c) => c.fits) ??
    // no room holds it all: the fewest rows whose width does, past the room in height
    candidates.find((c) => c.width <= roomW + 0.01) ??
    candidates[most - 1]!
  );
}
